/**
 * SteamBarrelCard — Live sensor display for a steam barrel
 *
 * Shows temperature (PV), setpoint (SP), output %, run state,
 * remaining time, full-cycle sparkline, and last update timestamp.
 */

import { useMemo, useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea
} from 'recharts'
import { Thermometer, AlertTriangle, Wifi, WifiOff, Zap } from 'lucide-react'
import { supabase } from '@/supabase-client'
import { useSensorData } from '@/hooks/useSensorData'
import { useBarrelEnergy } from '@/hooks/useBarrelEnergy'

function formatRelativeTime(date) {
  if (!date) return 'never'
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

function formatMinutes(minutes) {
  if (minutes == null) return '-'
  const m = Number(minutes)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`
}

const timeFormatter = (ts) =>
  new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

export default function SteamBarrelCard({ barrelId }) {
  // Detect cycle start from the N1200's run_state data.
  // Finds the last run_state=0 reading (controller off) — the cycle started just after.
  const [detectedCycleStart, setDetectedCycleStart] = useState(null)

  useEffect(() => {
    async function detectCycleStart() {
      const since = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
      const { data } = await supabase
        .from('sensor_readings')
        .select('recorded_at')
        .eq('device_type', 'steam_barrel')
        .eq('device_id', barrelId)
        .eq('metric', 'run_state')
        .eq('value', 0)
        .gte('recorded_at', since)
        .order('recorded_at', { ascending: false })
        .limit(1)

      if (data?.[0]) {
        setDetectedCycleStart(new Date(data[0].recorded_at))
      }
    }
    detectCycleStart()
  }, [barrelId])

  const { latest, history, historyMap, status, loading, lastUpdate } = useSensorData(
    'steam_barrel', barrelId,
    { historyMetrics: ['temperature', 'output_pct'], historySince: detectedCycleStart }
  )

  const temperature = latest.temperature?.value ?? null
  const setpoint = latest.setpoint?.value ?? null
  const outputPct = latest.output_pct?.value ?? null
  const runState = latest.run_state?.value ?? null
  const isRunning = runState === 1 || runState === '1'

  const remainingTime = status.remaining_time?.value != null ? Number(status.remaining_time.value) : null

  const { energyKwh, costPounds, avgRatePenceKwh } = useBarrelEnergy({
    status,
    outputHistory: historyMap?.output_pct,
    cycleStartOverride: detectedCycleStart
  })

  // Derive cycle timing for chart domain
  const cycleStart = detectedCycleStart
  const cycleEnd = useMemo(() => {
    if (remainingTime == null || !isRunning) return null
    return new Date(Date.now() + remainingTime * 60 * 1000)
  }, [remainingTime, isRunning])

  const isStale = useMemo(() => {
    if (!lastUpdate) return true
    return (Date.now() - new Date(lastUpdate).getTime()) > 60_000
  }, [lastUpdate])

  // Sparkline data — history is already scoped to cycle via historySince
  const sparklineData = useMemo(() => {
    if (!history || history.length === 0) return []

    // Filter out 0.0°C noise (N1200 reports zero when controller is off)
    const filtered = history.filter(r => r.value !== 0)

    if (filtered.length === 0) return []

    const step = Math.max(1, Math.floor(filtered.length / 100))
    return filtered.filter((_, i) => i % step === 0).map(r => ({
      v: r.value,
      ts: new Date(r.recorded_at).getTime()
    }))
  }, [history])

  const xDomain = useMemo(() => {
    if (cycleStart && cycleEnd) {
      return [cycleStart.getTime(), cycleEnd.getTime()]
    }
    return ['auto', 'auto']
  }, [cycleStart, cycleEnd])

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Thermometer className="h-4 w-4" />
            Barrel {barrelId}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
            Loading...
          </div>
        </CardContent>
      </Card>
    )
  }

  const hasData = temperature !== null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Thermometer className="h-4 w-4" />
            Barrel {barrelId}
          </CardTitle>
          <div className="flex items-center gap-2">
            {isRunning ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                Running
              </Badge>
            ) : hasData ? (
              <Badge variant="outline" className="text-xs">
                Stopped
              </Badge>
            ) : null}
            {isStale && hasData && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Stale
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-32 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
            <WifiOff className="h-5 w-5" />
            <span>No sensor data</span>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Temperature */}
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums">
                {temperature.toFixed(1)}
              </span>
              <span className="text-lg text-muted-foreground">°C</span>
            </div>

            {/* Setpoint / Output / Remaining */}
            <div className="grid grid-cols-3 gap-1 text-sm">
              {setpoint !== null && (
                <div className="text-muted-foreground">
                  Setpoint <span className="font-medium text-foreground tabular-nums">{setpoint.toFixed(1)}°C</span>
                </div>
              )}
              {outputPct !== null && (
                <div className="text-muted-foreground">
                  Output <span className="font-medium text-foreground tabular-nums">{outputPct.toFixed(0)}%</span>
                </div>
              )}
              {isRunning && remainingTime !== null && (
                <div className="text-muted-foreground">
                  Remaining <span className="font-medium text-foreground tabular-nums">{formatMinutes(remainingTime)}</span>
                </div>
              )}
            </div>

            {/* Energy stats — visible during active cycle */}
            {isRunning && energyKwh > 0 && (
              <div className="grid grid-cols-3 gap-1 text-sm">
                <div className="text-muted-foreground">
                  <Zap className="h-3 w-3 inline mr-0.5" />
                  <span className="font-medium text-foreground tabular-nums">{energyKwh.toFixed(1)} kWh</span>
                </div>
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground tabular-nums">{avgRatePenceKwh.toFixed(1)}p/kWh</span>
                </div>
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground tabular-nums">£{costPounds.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Temperature chart */}
            <div className="h-24 p-2.5">
              {sparklineData.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sparklineData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                    <XAxis
                      dataKey="ts"
                      type="number"
                      domain={xDomain}
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={40}
                      tickFormatter={timeFormatter}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      domain={[(min) => Math.floor(min - 2), (max) => Math.ceil(max + 2)]}
                      width={40}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        const { v, ts } = payload[0].payload
                        return (
                          <div className="rounded-md bg-white px-2 py-1 text-xs shadow-md border">
                            <span className="font-medium tabular-nums">{v.toFixed(1)}°C</span>
                            <span className="text-muted-foreground ml-1.5">{timeFormatter(ts)}</span>
                          </div>
                        )
                      }}
                    />
                    {/* Future shading */}
                    {cycleEnd && (
                      <ReferenceArea
                        x1={Date.now()}
                        x2={cycleEnd.getTime()}
                        fill="#f3f4f6"
                        fillOpacity={0.5}
                      />
                    )}
                    {/* "End" marker */}
                    {cycleEnd && (
                      <ReferenceLine
                        x={cycleEnd.getTime()}
                        stroke="#ef4444"
                        strokeDasharray="5 3"
                        strokeWidth={1}
                        label={{ value: 'End', position: 'top', fontSize: 9, fill: '#ef4444' }}
                      />
                    )}
                    {/* "Now" marker */}
                    {cycleStart && cycleEnd && (
                      <ReferenceLine
                        x={Date.now()}
                        stroke="#ef4444"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={{ value: 'Now', position: 'top', fontSize: 9, fill: '#ef4444' }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke="hsl(var(--foreground))"
                      strokeWidth={1.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-md">
                  Awaiting temperature data...
                </div>
              )}
            </div>

            {/* Last update */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Wifi className="h-3 w-3" />
              <span>{formatRelativeTime(lastUpdate)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
